/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { useContext } from 'react';
import DiviFieldCard from '../../../customs/DiviFieldCard/DiviFieldCard';
import { CardId, getCardTitle } from '../../../../models/diviCard';
import style from './PatientData.scss?inline';
import globalstyle from '../../../../global.scss?inline';
import { useCSS } from '../../../../provider/CSSProvider';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';
import { useFreshCallback } from '../../../../utils/hooks';
import { Draft } from 'immer';
import {
  BirthDateBackendFormat,
  EXTENDED_PATIENT_DATA_RECORD_ELEMENT_KEY,
  ExtendedPatientDataRecord,
  getAgeFromBirthday,
  getPseudonymTuplesFor,
  PseudonymKeys,
} from '../../../../models/genericElements/extendedPatientData';
import { InputState } from '../../../../backendModels/general.model';
import { NodeType } from '../../../../backendModels/report.model';
import { isGenericRecordDeletable } from '../../../../models/generic';
import CheckboxList from '../../../customs/checkboxList/CheckboxList';
import dayjs from 'dayjs';
import {
  PATIENT_DATA_RECORD_ELEMENT_KEY,
  PatientDataRecord,
  PatientPropertyKeys,
} from '../../../../models/genericElements/patientData';
import { changePropertyAndMarkTouched, getTouchedClassName } from '../../../../utils/recordWithTouched';
import {
  getPrivatelyInsuredTuplesFor,
  PRIVATE_HEALTH_INSURANCE_RECORD_ELEMENT_KEY,
  PrivateHealthInsuranceRecord,
  PrivatelyInsuredKeys,
} from '../../../../models/genericElements/privateHealthInsurance';
import { Typography } from '@mui/material';
import _ from 'lodash';
import { DatePicker, DatePickerFormat } from '../../../customs/datepicker/DatePicker';
import DebouncedTextField from '../../../customs/debouncedTextField/DebouncedTextField';
import { nonEmptyString } from '../../../../utils/util';
import { ApplicationConfig } from '../../../../config/application.config';

interface PatientDataProps {
  nodeType: NodeType;
}

export default function PatientData({ nodeType }: PatientDataProps) {
  useCSS(style);
  useCSS(globalstyle);

  const { findRecordOrDefault, patient, patientUpdate, adaptRecord } = useContext(ReportsAPIContext);

  const patientRecord = findRecordOrDefault('generic', nodeType, PATIENT_DATA_RECORD_ELEMENT_KEY) as PatientDataRecord;

  const privateHealthInsuranceRecord = findRecordOrDefault(
    'generic',
    nodeType,
    PRIVATE_HEALTH_INSURANCE_RECORD_ELEMENT_KEY
  ) as PrivateHealthInsuranceRecord;

  const extendedRecord = findRecordOrDefault(
    'generic',
    nodeType,
    EXTENDED_PATIENT_DATA_RECORD_ELEMENT_KEY
  ) as ExtendedPatientDataRecord;

  const birthDate =
    patientRecord.values?.birthday?.text != null
      ? (dayjs(patientRecord.values?.birthday?.text, BirthDateBackendFormat).format(DatePickerFormat) ?? '')
      : '';

  const handleBirthDateChange = useFreshCallback((value: string) => {
    const newBirthdateDayjs = value !== '' ? dayjs(value, DatePickerFormat) : null;

    const newBirthdateBackend = newBirthdateDayjs?.isValid() ? newBirthdateDayjs?.format(BirthDateBackendFormat) : null;

    let [age, newbornAge] = getAgeFromBirthday(newBirthdateDayjs);

    const now = dayjs().set('hour', 0).set('minute', 0);
    if (now.isBefore(newBirthdateDayjs)) {
      age = 0;
    }

    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<ExtendedPatientDataRecord>, deleteRecord) => {
        draft.inputState = InputState.ENTERED;

        if (newBirthdateBackend != null) {
          if (newbornAge != null) {
            draft.values = {
              ...draft.values,
              newbornAge: {
                fieldType: 'singleSelect',
                option: newbornAge,
              },
            };
          } else {
            delete draft.values?.newbornAge;
          }
        } else {
          if (patientRecord.values?.birthday) {
            // If there has been a birthday before, we have automatically calculated the age and need to delete it.
            delete draft.values?.newbornAge;
          }
        }

        if (isGenericRecordDeletable(draft)) {
          deleteRecord();
        }
      },
      EXTENDED_PATIENT_DATA_RECORD_ELEMENT_KEY
    );

    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<PatientDataRecord>, deleteRecord) => {
        draft.inputState = InputState.ENTERED;

        if (!_.isEqual(newBirthdateBackend, draft.values?.birthday ?? null)) {
          draft.values = {
            ...draft.values,
            touched: {
              options: _.union([PatientPropertyKeys.birthday], draft.values?.touched?.options ?? []),
              fieldType: 'multiSelect',
            },
          };

          if (age != null) {
            draft.values = {
              ...draft.values,
              touched: {
                options: _.union([PatientPropertyKeys.age], draft.values?.touched?.options ?? []),
                fieldType: 'multiSelect',
              },
            };
          }
        }

        if (newBirthdateBackend != null) {
          draft.values = {
            ...draft.values,
            birthday: {
              fieldType: 'text',
              text: newBirthdateBackend,
            },
          };

          if (age != null) {
            draft.values = {
              ...draft.values,
              age: {
                fieldType: 'numeric',
                number: age,
              },
            };
          } else {
            delete draft.values?.age;
          }
        } else {
          if (draft.values?.birthday) {
            // If there has been a birthday before, we have automatically calculated the age and need to delete it.
            delete draft.values?.age;
          }
          delete draft.values?.birthday;
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      PATIENT_DATA_RECORD_ELEMENT_KEY
    );
  });

  const handlePseudonymChange = useFreshCallback((value: PseudonymKeys[]) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<ExtendedPatientDataRecord>, deleteRecord) => {
        draft.inputState = InputState.ENTERED;
        if (value.length > 0) {
          draft.values = {
            ...draft.values,
            pseudonym: {
              fieldType: 'multiSelect',
              options: value,
            },
          };
        } else {
          delete draft.values?.pseudonym;
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      EXTENDED_PATIENT_DATA_RECORD_ELEMENT_KEY
    );
  });

  const handlePrivatelyInsuredChange = useFreshCallback((value: PrivatelyInsuredKeys[]) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<PrivateHealthInsuranceRecord>, deleteRecord) => {
        draft.inputState = InputState.ENTERED;
        if (value.length > 0) {
          draft.values = {
            ...draft.values,
            privatelyInsured: {
              fieldType: 'multiSelect',
              options: value,
            },
          };
        } else {
          delete draft.values?.privatelyInsured;
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      PRIVATE_HEALTH_INSURANCE_RECORD_ELEMENT_KEY
    );
  });

  function handleChangedPatientProperty(field: PatientPropertyKeys, value: string) {
    changePropertyAndMarkTouched(
      adaptRecord,
      nodeType,
      PATIENT_DATA_RECORD_ELEMENT_KEY,
      field,
      value !== '' ? { fieldType: 'text', text: value } : undefined
    );
  }

  const handleSave = (propertyKey: PatientPropertyKeys, value: string | undefined) => {
    if (nonEmptyString(value)) {
      patientUpdate?.({
        ...patient,
        [propertyKey]: value,
      });
    } else {
      patientUpdate?.(_.omit(patient, [propertyKey]));
    }
  };

  const handleBirthdaySave = (newValue: string | undefined) => {
    let changedBirthday = '';
    let changedAge = 0;

    if (nonEmptyString(newValue)) {
      changedBirthday = formatBirthday(newValue);

      const [age] = getAgeFromBirthday(dayjs(changedBirthday));
      changedAge = age ?? 0;
      patientUpdate?.({
        ...patient,
        birthday: changedBirthday,
        age: changedAge,
      });
    } else {
      patientUpdate?.(_.omit(patient, ['birthday', 'age']));
    }
  };

  const titleContent = (
    <div className='patient-title-content'>
      <Typography align='left' variant='h2'>
        {getCardTitle(CardId.PatientData)}
      </Typography>
      <CheckboxList
        className='privately-insured'
        items={getPrivatelyInsuredTuplesFor([PrivatelyInsuredKeys.isPrivatelyInsured])}
        selectedValues={
          privateHealthInsuranceRecord.inputState === InputState.ENTERED
            ? (privateHealthInsuranceRecord.values.privatelyInsured?.options ?? [])
            : []
        }
        onValuesChange={handlePrivatelyInsuredChange}
      />
    </div>
  );

  return (
    <DiviFieldCard cardType={CardId.PatientData} customTitleContent={titleContent}>
      <div className='patient-data-wireframe'>
        <DebouncedTextField
          className={getTouchedClassName(patientRecord, PatientPropertyKeys.surname)}
          label={'Name'}
          autoComplete='off'
          value={patientRecord.values?.surname?.text ?? ''}
          onDebounceChange={(value) => handleChangedPatientProperty(PatientPropertyKeys.surname, value)}
          onUnfocus={(value) => handleSave(PatientPropertyKeys.surname, value)}
        />
        <DebouncedTextField
          className={getTouchedClassName(patientRecord, PatientPropertyKeys.name)}
          label={'Vorname'}
          autoComplete='off'
          value={patientRecord.values?.name?.text ?? ''}
          onDebounceChange={(value) => handleChangedPatientProperty(PatientPropertyKeys.name, value)}
          onUnfocus={(value) => handleSave(PatientPropertyKeys.name, value)}
        />
        <DatePicker
          className={`no-margin-top ${getTouchedClassName(patientRecord, PatientPropertyKeys.birthday)}`}
          label='Geburtsdatum'
          value={birthDate}
          onChange={handleBirthDateChange}
          onUnFocus={ApplicationConfig.immediatelySyncToConference ? handleBirthdaySave : undefined}
        />
        <CheckboxList
          className='symptoms-list'
          items={getPseudonymTuplesFor([PseudonymKeys.pseudonym])}
          selectedValues={
            extendedRecord.inputState === InputState.ENTERED ? (extendedRecord.values.pseudonym?.options ?? []) : []
          }
          onValuesChange={handlePseudonymChange}
        />
        <DebouncedTextField
          className={getTouchedClassName(patientRecord, PatientPropertyKeys.street)}
          label={'Straße + Hausnummer'}
          autoComplete='off'
          value={patientRecord.values?.street?.text ?? ''}
          onDebounceChange={(value) => handleChangedPatientProperty(PatientPropertyKeys.street, value)}
          onUnfocus={(value) => handleSave(PatientPropertyKeys.street, value)}
        />
        <div className='row patient-data-location'>
          <DebouncedTextField
            className={getTouchedClassName(patientRecord, PatientPropertyKeys.postalCode)}
            label={'PLZ'}
            fullWidth
            autoComplete='off'
            type='number'
            slotProps={{ htmlInput: { min: 1, max: 99999, step: 1, maxLength: 5 } }}
            value={patientRecord.values?.postalCode?.text ?? ''}
            onDebounceChange={(value) => handleChangedPatientProperty(PatientPropertyKeys.postalCode, value)}
            onUnfocus={(value) => handleSave(PatientPropertyKeys.postalCode, value)}
          />
          <DebouncedTextField
            className={`patient-data-city ${getTouchedClassName(patientRecord, PatientPropertyKeys.city)}`}
            label={'Ort'}
            fullWidth
            value={patientRecord.values?.city?.text ?? ''}
            onDebounceChange={(value) => handleChangedPatientProperty(PatientPropertyKeys.city, value)}
            onUnfocus={(value) => handleSave(PatientPropertyKeys.city, value)}
          />
        </div>
        <div className='row insurance-data-location'>
          <DebouncedTextField
            className={getTouchedClassName(patientRecord, PatientPropertyKeys.insuranceName)}
            label={'Kasse'}
            value={patientRecord.values?.insuranceName?.text ?? ''}
            onDebounceChange={(value) => handleChangedPatientProperty(PatientPropertyKeys.insuranceName, value)}
            onUnfocus={(value) => handleSave(PatientPropertyKeys.insuranceName, value)}
          />
          <DebouncedTextField
            className={getTouchedClassName(patientRecord, PatientPropertyKeys.insuranceNumber)}
            label={'Kasse / Nr.'}
            value={patientRecord.values?.insuranceNumber?.text ?? ''}
            onDebounceChange={(value) => handleChangedPatientProperty(PatientPropertyKeys.insuranceNumber, value)}
            onUnfocus={(value) => handleSave(PatientPropertyKeys.insuranceNumber, value)}
          />
        </div>
        <DebouncedTextField
          className={getTouchedClassName(patientRecord, PatientPropertyKeys.policyNumber)}
          label={'Versichertennummer'}
          value={patientRecord.values?.policyNumber?.text ?? ''}
          onDebounceChange={(value) => handleChangedPatientProperty(PatientPropertyKeys.policyNumber, value)}
          onUnfocus={(value) => handleSave(PatientPropertyKeys.policyNumber, value)}
        />
      </div>
    </DiviFieldCard>
  );
}

function formatBirthday(date: string): string {
  return date.replaceAll('-', '/');
}
